import { api } from './client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MijnWerkSegment {
  system: string;
  m2: number;
  rate: number;
  amount: number;
  /** The PO line's own text, markers stripped. */
  description: string;
}

export interface MijnWerkCollector {
  collectorId: number;
  taskId: number;
  /**
   * The PO line a flag on this card is anchored to. A collector billed as several
   * systems is several lines but one card, so the card's first line carries the
   * flag for the whole collector — which is what the crew means anyway.
   */
  poLineId: number;
  /** Built from the Odoo task, which may have been renamed since the PO was written. */
  label: string;
  /** What the PO line itself says — one text per segment, newline separated. */
  description: string;
  blok: string;
  verdieping: string;
  m2: number;
  system: string;
  /** 0 for multi-segment collectors — read per-segment rates from `segments` instead */
  rate: number;
  amount: number;
  segments?: MijnWerkSegment[];
}

export interface MijnWerkMeerwerk {
  poLineId: number;
  type: string;
  label: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

/** Any PO line that is not a collector, a meerwerk or a minprijs. */
export interface MijnWerkOther {
  poLineId: number;
  label: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface MijnWerkMinprijs {
  poLineId: number;
  count: number;
  unitPrice: number;
  amount: number;
  description: string;
}

export interface MijnWerkWerf {
  projectId: number | null;
  projectName: string;
  sNumber: string | null;
  /** true only when the caller is personally a project member → safe to tap through */
  tappable: boolean;
}

export interface MijnWerkGroup {
  /** YYYY-MM-DD, or null when the PO line carries no date. Sorted last. */
  date: string | null;
  werf: MijnWerkWerf;
  collectors: MijnWerkCollector[];
  meerwerken: MijnWerkMeerwerk[];
  minprijs: MijnWerkMinprijs[];
  other: MijnWerkOther[];
  dayTotalM2: number;
  dayTotal: number;
}

/** One selectable purchase order. One PO = one billing period. */
export interface MijnWerkPoRef {
  id: number;
  name: string;
  /** true = the open rolling PO, still accumulating work and not yet approved */
  isOpen: boolean;
  /** date_order, YYYY-MM-DD */
  startedAt: string | null;
  /** Odoo's own ex-VAT header total for this PO. */
  poTotal: number;
}

/** The PO currently shown, with the date span of the work it actually carries. */
export interface MijnWerkPo extends MijnWerkPoRef {
  dateFrom: string | null;
  dateTo: string | null;
}

/** A line the subcontractor flagged. One per line, appended to while it is open. */
export interface MijnWerkMelding {
  activityId: number;
  poLineId: number;
  /** open = the office still has it as a to-do. */
  status: 'open' | 'afgehandeld';
  /** Everything written about this line so far, oldest first. */
  text: string;
  /** The office's closing comment, when they left one. */
  resolution: string | null;
  createdAt: string | null;
  resolvedAt: string | null;
}

export interface MijnWerkResponse {
  isOa: boolean;
  vendorName: string | null;
  /** The PO shown. Null when this OA has no selectable PO at all. */
  po: MijnWerkPo | null;
  /** Every PO the OA may open, newest first — the open one plus confirmed ones. */
  pos: MijnWerkPoRef[];
  groups: MijnWerkGroup[];
  /** Flags raised on this PO, resolved ones included. Empty when the read failed. */
  meldingen: MijnWerkMelding[];
  totalM2: number;
  /**
   * The PO's own header total from Odoo — the figure to invoice. Odoo rounds it once
   * over the whole order and each line separately, so it can sit a few cents away
   * from `linesTotal`; that gap is Odoo's own and shows up in Odoo too.
   */
  total: number;
  /** Sum of the lines listed below. Diagnostic, not the invoice figure. */
  linesTotal: number;
  /** What the total is made of — answers "is it right?" without reading every line. */
  categories: MijnWerkCategories;
}

export interface MijnWerkCategories {
  installation: number;
  installationCount: number;
  extras: number;
  extrasCount: number;
  deductions: number;
  deductionsCount: number;
  other: number;
  otherCount: number;
}

// ── API call ───────────────────────────────────────────────────────────────────

/** Omit `poId` to get the open PO, or the newest one when nothing is open. */
export async function fetchMijnWerk(poId?: number): Promise<MijnWerkResponse> {
  const qs = poId != null ? `?poId=${poId}` : '';
  return api<MijnWerkResponse>(`/api/onderaannemers/mijn-werk${qs}`);
}

/** Flag one PO line. A second comment while the first is open extends it. */
export async function postMelding(poLineId: number, text: string): Promise<MijnWerkMelding> {
  const res = await api<{ melding: MijnWerkMelding }>('/api/onderaannemers/mijn-werk/melding', {
    method: 'POST',
    body: JSON.stringify({ poLineId, text }),
  });
  return res.melding;
}
